import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input, InputNumber, message, Modal, Button, Space, Select } from "antd";
import {
  SearchOutlined, EyeOutlined, RollbackOutlined,
  StopOutlined, PrinterOutlined, ShopOutlined,
  EditOutlined, DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient, getStorageUrl } from "../../../shared/api/AllApi";
import { getApiErrorMessage } from "../../../shared/api/errors";
import { parseLocalizedName } from "../../../utils/localization";
import { formatCurrency, orderProductSubtotal } from "../../../utils/formatters";
import { formatOrderNo } from "../../../shared/lib/documentNumbers";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { formatRangeParams } from "../../../shared/lib/dateFilterPresets";
import { createRequestGuard, isAbortError } from "../../../shared/lib/request";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import usePermissions from "../../../hooks/usePermissions";
import useUserShift from "../../../hooks/useUserShift";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import {
  OFFLINE_QUEUE_CHANGED_EVENT,
  listOfflineSales,
  offlineSaleToListItem,
  syncPendingOfflineSales,
} from "../lib/offlineSaleQueue";
import { isNetworkError } from "../../../shared/lib/connectivity";

const PosSubOrders = ({ onBack, onReprint }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { locked: shiftLocked, hoursLabel: shiftHours } = useUserShift();
  const currency = readCachedSystemConfig()?.currency_code || "IQD";
  const canReturn = hasPermission("pos.return") && !shiftLocked;
  const canCancel = hasPermission("pos.cancel") && !shiftLocked;
  const canEdit = hasPermission("pos.edit") && !shiftLocked;
  const canDelete = hasPermission("pos.delete") && !shiftLocked;

  const [deleting, setDeleting] = useState(false);

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const [h, m] = String(readCachedSystemConfig()?.business_day_start || "00:00").split(":");
    const cutoffMin = (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
    const d = dayjs().subtract(cutoffMin, "minute");
    return [d.startOf("day"), d.endOf("day")];
  });
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [returning, setReturning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [cancelRefundMethod, setCancelRefundMethod] = useState("cash");
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnNote, setReturnNote] = useState("");
  const [returnQtys, setReturnQtys] = useState({});
  const [returnWeights, setReturnWeights] = useState({});
  const [returnReasons, setReturnReasons] = useState({});
  const [returnRefundMethod, setReturnRefundMethod] = useState("cash");
  const listGuardRef = useRef(createRequestGuard());
  const detailGuardRef = useRef(createRequestGuard());

  const loadSales = useCallback(() => {
    const req = listGuardRef.current.next();
    setLoading(true);
    const params = {
      ...formatRangeParams(dateRange, { withTime: true, fromKey: "date_from", toKey: "date_to" }),
    };

    const mergePending = async (serverSales) => {
      const pending = await listOfflineSales();
      const pendingItems = pending.map(offlineSaleToListItem);
      // Newest first: pending (local) on top, then server sales
      return [...pendingItems, ...(Array.isArray(serverSales) ? serverSales : [])];
    };

    apiClient.get("/pos/sales", { params, signal: req.signal })
      .then(async (r) => {
        if (!req.isCurrent()) return;
        const merged = await mergePending(Array.isArray(r.data) ? r.data : []);
        if (!req.isCurrent()) return;
        setSales(merged);
      })
      .catch(async (e) => {
        if (isAbortError(e)) return;
        if (!req.isCurrent()) return;
        if (isNetworkError(e)) {
          const merged = await mergePending([]);
          if (req.isCurrent()) setSales(merged);
          return;
        }
        setSales([]);
      })
      .finally(() => {
        if (req.isCurrent()) setLoading(false);
      });
  }, [dateRange]);

  useEffect(() => {
    loadSales();
    const onQueue = () => loadSales();
    window.addEventListener(OFFLINE_QUEUE_CHANGED_EVENT, onQueue);
    return () => {
      listGuardRef.current.abort();
      detailGuardRef.current.abort();
      window.removeEventListener(OFFLINE_QUEUE_CHANGED_EVENT, onQueue);
    };
  }, [loadSales]);

  const openDetail = async (sale) => {
    if (sale?._pendingSync) {
      const items = (sale.receipt?.items || []).map((line, idx) => ({
        id: `local-${idx}`,
        product_id: line.id,
        quantity: line.qty,
        weight: line.weight,
        single_price: line.price,
        unit_price: line.price,
        total_price: line.subtotal != null
          ? line.subtotal
          : (line.price || 0) * ((line.weight > 0 ? line.weight : line.qty) || 0),
        product: { name: line.name },
        vendor_id: line.vendorId || null,
      }));
      setSelected({
        ...sale,
        order_products: items,
        items,
        total_price: sale.total_price,
        commission_amount: sale.commission_amount,
        is_taxed: sale.is_taxed,
      });
      return;
    }

    const req = detailGuardRef.current.next();
    setLoadingDetail(true);
    try {
      const r = await apiClient.get(`/pos/sales/${sale.id}`, { signal: req.signal });
      if (!req.isCurrent()) return;
      setSelected(r.data);
    } catch (e) {
      if (isAbortError(e)) return;
      if (req.isCurrent()) message.error(t("posOrdersFailedLoad", "Failed to load sale details"));
    } finally {
      if (req.isCurrent()) setLoadingDetail(false);
    }
  };

  const detailItems = selected ? (selected.order_products || selected.items || []) : [];

  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  // Cases (qty) + kg from the sale line — never hide either field
  const soldQtyOf = (it) => num(it.sold_qty ?? it.quantity);
  const soldWeightOf = (it) => num(it.sold_weight ?? it.weight);
  const availableQty = (it) => {
    if (it.available_qty != null && it.available_qty !== "") return Math.max(0, num(it.available_qty));
    return soldQtyOf(it);
  };
  const availableWeight = (it) => {
    if (it.available_weight != null && it.available_weight !== "") return Math.max(0, num(it.available_weight));
    return soldWeightOf(it);
  };
  const availableAmount = (it) => {
    if (it.available_amount != null && it.available_amount !== "") return Math.max(0, num(it.available_amount));
    const w = soldWeightOf(it);
    return w > 0 ? w : soldQtyOf(it);
  };
  const unitPriceOf = (it) => num(it.single_price ?? it.unit_price);
  const billingReturnOf = (it) => {
    const w = Math.min(num(returnWeights[it.id]), availableWeight(it));
    const q = Math.min(num(returnQtys[it.id]), availableQty(it));
    // Money follows sale billing: kg when the line has weight, else cases
    if (soldWeightOf(it) > 0) return w;
    return q;
  };
  const lineProductRefund = (it) => {
    const sold = soldWeightOf(it) > 0 ? soldWeightOf(it) : soldQtyOf(it);
    const billing = billingReturnOf(it);
    const lineTotal = num(it.total_price);
    if (sold > 0 && lineTotal > 0) return lineTotal * (billing / sold);
    return billing * unitPriceOf(it);
  };

  const returnableItems = detailItems.filter(
    (it) => availableAmount(it) > 0 || availableQty(it) > 0 || availableWeight(it) > 0
  );
  const productCashBack = returnableItems.reduce((s, it) => s + lineProductRefund(it), 0);
  const linesBase = detailItems.reduce((s, it) => s + num(it.total_price), 0);
  const orderTotal = num(selected?.total_price);
  const storedComm = num(selected?.commission_amount);
  // Prefer stored commission; if missing, derive from total − product lines
  const orderComm = storedComm > 0 ? storedComm : Math.max(0, orderTotal - linesBase);
  const orderBase = Math.max(0.01, linesBase > 0 ? linesBase : orderTotal - orderComm);
  const commissionCashBack = orderComm > 0 ? orderComm * (productCashBack / orderBase) : 0;
  const returnCashBack = productCashBack + commissionCashBack;
  const refundableCollected = Math.max(
    0,
    num(selected?.amount_paid) - num(selected?.refunded_amount)
  );
  const estimatedCashRefund = Math.min(returnCashBack, refundableCollected);
  const estimatedCreditReduction = Math.max(0, returnCashBack - estimatedCashRefund);
  const hasReturnable = returnableItems.length > 0;

  const openReturnModal = () => {
    const initQ = {};
    const initW = {};
    const reasons = {};
    detailItems.forEach((it) => {
      initQ[it.id] = 0;
      initW[it.id] = 0;
      reasons[it.id] = t("posOrdersDefaultReturnReason", "Customer return");
    });
    setReturnQtys(initQ);
    setReturnWeights(initW);
    setReturnReasons(reasons);
    setReturnNote("");
    setReturnRefundMethod(selected?.payment_method === "cash" ? "cash" : "credit");
    setReturnModalOpen(true);
  };

  const handleReturn = async () => {
    if (!selected) return;
    const items = detailItems
      .map((it) => {
        const rq = Math.min(parseFloat(returnQtys[it.id] || 0), availableQty(it));
        const rw = Math.min(parseFloat(returnWeights[it.id] || 0), availableWeight(it));
        return {
          order_product_id: it.id,
          product_id: it.product_id,
          return_qty: rq,
          return_weight: rw,
          reason: returnReasons[it.id] || returnNote || null,
        };
      })
      .filter((x) => x.return_qty > 0 || x.return_weight > 0);

    if (items.length === 0) {
      message.warning(t("posOrdersReturnNothing", "Enter a quantity or weight to return"));
      return;
    }

    setReturning(true);
    try {
      const response = await apiClient.post(`/pos/sales/${selected.id}/return`, {
        items,
        note: returnNote,
        refund_method: returnRefundMethod,
      });
      const cash = Number(response.data?.cash_refund_amount || 0);
      const credit = Number(response.data?.credit_reduction_amount || 0);
      const totalRefund = Number(response.data?.refund_amount || cash + credit);
      const commPart = Number(response.data?.commission_refund_amount || 0);
      message.success(
        `${t("posOrdersReturnSuccess", "Return recorded")}: ${formatCurrency(totalRefund, currency)}`
        + (commPart > 0 ? ` (${t("posTerminalCommission", "Comm.")} ${formatCurrency(commPart, currency)})` : "")
        + ` · ${t("refunded", "refunded")}: ${formatCurrency(cash, currency)}`
        + ` · ${t("debtReduced", "debt reduced")}: ${formatCurrency(credit, currency)}`
      );
      setReturnModalOpen(false);
      setReturnNote("");
      // Refresh the detail so the "available" amounts update (original sale is unchanged)
      try {
        const r = await apiClient.get(`/pos/sales/${selected.id}`);
        setSelected(r.data);
      } catch { /* ignore */ }
      loadSales();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("posOrdersReturnFailed", "Return failed"), t));
    } finally {
      setReturning(false);
    }
  };

  const handleCancel = async () => {
    if (!selected) return;
    if (cancelNote.trim().length < 3) {
      message.warning(t("posOrdersCancelReasonRequired", "Enter a cancellation reason"));
      return;
    }
    setCancelling(true);
    try {
      const response = await apiClient.post(`/pos/sales/${selected.id}/cancel`, {
        note: cancelNote.trim(),
        refund_method: cancelRefundMethod,
      });
      message.success(`${t("posOrdersCancelSuccess", "Sale cancelled")} · ${t("refunded", "Refunded")}: ${formatCurrency(response.data?.refund_amount || 0, currency)}`);
      setSelected(response.data?.order || ((prev) => ({ ...prev, status: "cancelled", cancellation_reason: cancelNote })));
      setCancelModalOpen(false);
      setCancelNote("");
      loadSales();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("posOrdersCancelFailed", "Cancel failed"), t));
    } finally {
      setCancelling(false);
    }
  };

  const openFullEdit = () => {
    if (!selected?.id) return;
    navigate(`/orders/${selected.id}/edit`);
  };

  const handleDelete = () => {
    if (!selected) return;
    Modal.confirm({
      title: t("posOrdersDeleteConfirm", "Delete this sale?"),
      content: t("posOrdersDeleteHint", "Only an unpaid draft can be archived. Paid or confirmed sales must be cancelled or returned."),
      okText: t("archive", "Archive"),
      okButtonProps: { danger: true },
      cancelText: t("cancel", "Cancel"),
      zIndex: 4000,
      onOk: async () => {
        setDeleting(true);
        try {
          await apiClient.delete(`/pos/sales/${selected.id}`, { data: { reason: "Archived from POS order list" } });
          message.success(t("posOrdersDeleteSuccess", "Sale archived"));
          setSelected(null);
          loadSales();
        } catch (e) {
          message.error(getApiErrorMessage(e, t("posOrdersDeleteFailed", "Delete failed"), t));
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const filtered = sales.filter((s) => {
    const term = search.toLowerCase();
    if (!term) return true;
    const customer = s.customer?.name || "";
    const id = String(s.id);
    const doc = String(s.document_no || "");
    return (
      customer.toLowerCase().includes(term) ||
      id.includes(term) ||
      doc.toLowerCase().includes(term)
    );
  });

  const paymentLabel = (method) =>
    method === "cash"
      ? t("posOrdersCash", "Cash")
      : method === "credit"
      ? t("posOrdersCredit", "Credit")
      : method === "card"
      ? t("posOrdersCard", "Card")
      : method === "bank"
      ? t("posOrdersBank", "Bank")
      : (method || "—");

  // POS sales are cash/credit only — keep refund method options consistent.
  const posPaymentOptions = [
    { value: "cash", label: paymentLabel("cash") },
    { value: "credit", label: paymentLabel("credit") },
  ];

  const statusBadge = (status, method) => {
    if (status === "pending_sync")
      return <span className="pos-badge pos-badge-warning">{t("posOrdersStatusPendingSync", "PENDING SYNC")}</span>;
    if (status === "returned")
      return <span className="pos-badge pos-badge-danger">{t("posOrdersStatusReturned", "RETURNED")}</span>;
    if (status === "cancelled")
      return <span className="pos-badge pos-badge-muted">{t("posOrdersStatusCancelled", "CANCELLED")}</span>;
    if (method === "cash")
      return <span className="pos-badge pos-badge-success">{t("posOrdersStatusPaidCash", "PAID · CASH")}</span>;
    if (method === "credit")
      return <span className="pos-badge pos-badge-warning">{t("posOrdersStatusCreditUnpaid", "CREDIT · UNPAID")}</span>;
    // Unknown / missing method — don't invent "credit"
    return <span className="pos-badge pos-badge-muted">{paymentLabel(method)}</span>;
  };

  return (
      <div className="pos-sub-view">
      {shiftLocked && (
        <div style={{ background: "#9f1239", color: "#fff", padding: "8px 14px", fontWeight: 700, fontSize: 13, borderRadius: 8, marginBottom: 10 }}>
          {t(
            "outsideShiftBanner",
            "Outside working hours ({{hours}}). Selling, returns, edit, cancel and delete are blocked. You can still view data and log out.",
            { hours: shiftHours }
          )}
        </div>
      )}
      <div className="pos-sub-header">
        <div className="pos-sub-header-left">
          <div className="pos-sub-icon"><ShopOutlined /></div>
          <h2 className="pos-sub-title">{t("posOrdersTitle", "Sales Orders")}</h2>
          <span className="pos-sub-tab-badge" style={{ fontSize: 12, padding: "2px 10px" }}>{sales.length}</span>
        </div>
        <button type="button" className="pos-sub-back" onClick={onBack}>{t("posBackToPOS", "Back to POS")}</button>
      </div>

      <div className="pos-sub-toolbar">
        <Space wrap size={[8, 8]} style={{ width: "100%" }}>
          <DateRangeFilter
            value={dateRange}
            onChange={(v) => setDateRange(v && v[0] && v[1] ? v : [dayjs().startOf("day"), dayjs().endOf("day")])}
            showTime
            allowClear={false}
            size="small"
          />
          <Input
            prefix={<SearchOutlined />}
            placeholder={t("posOrdersSearch", "Search by customer name or order #...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            className="pos-sub-search"
            style={{ flex: 1, minWidth: 180 }}
          />
        </Space>
      </div>

      <div className="pos-sub-list">
        {loading ? (
          <div className="pos-sub-empty"><div className="pos-sub-empty-title">{t("posLoading", "Loading...")}</div></div>
        ) : filtered.length === 0 ? (
          <div className="pos-sub-empty">
            <div className="pos-sub-empty-icon"><ShopOutlined /></div>
            <div className="pos-sub-empty-title">{t("posOrdersNoSales", "No sales yet")}</div>
            <div className="pos-sub-empty-sub">{t("posOrdersNoSalesSub", "Completed sales will appear here")}</div>
          </div>
        ) : (
          <div className="pos-orders-list">
            {filtered.map((sale) => (
              <div
                key={sale.id}
                className={`pos-orders-row${sale.status === "returned" ? " returned" : ""}`}
                onClick={() => openDetail(sale)}
              >
                <div className="pos-orders-row-left">
                  <div className="pos-orders-id">{sale.document_no || formatOrderNo(sale.id, sale.created_at)}</div>
                  <div className="pos-orders-meta">
                    <span className="pos-orders-customer">
                      {sale.customer?.name || <span style={{ color: "#9ca3af" }}>{t("posWalkIn", "Walk-in")}</span>}
                    </span>
                    <span className="pos-orders-date">
                      {new Date(sale.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="pos-orders-row-right">
                  <div className="pos-orders-total">{formatCurrency(sale.total_price, currency)}</div>
                  {!(sale.is_taxed === false || sale.is_taxed === 0) && Number(sale.commission_amount) > 0 && (
                    <div style={{ fontSize: 11, color: "#d97706", fontWeight: 600, marginTop: 2 }}>
                      +{formatCurrency(sale.commission_amount, currency)} {t("posTerminalCommission", "Comm.")}
                    </div>
                  )}
                  <div style={{ marginTop: 4 }}>{statusBadge(sale.status, sale.payment_method)}</div>
                </div>
                <button type="button" className="pos-sub-edit-btn" style={{ marginLeft: 8 }}>
                  <EyeOutlined />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Return management modal — record a return per item (original sale is untouched) */}
      <Modal
        open={returnModalOpen}
        onCancel={() => setReturnModalOpen(false)}
        title={<span style={{ color: "var(--pos-danger)" }}><RollbackOutlined /> {t("posOrdersReturnTitle", "Return Management")} — {selected?.document_no || formatOrderNo(selected?.id, selected?.created_at)}</span>}
        okText={t("posOrdersReturnConfirm", "Process Returns")}
        okButtonProps={{ danger: true, loading: returning, disabled: returnCashBack <= 0 }}
        onOk={handleReturn}
        zIndex={4000}
        width={720}
        className="pos-sub-modal"
        rootClassName="pos-sub-modal-root"
      >
        <p style={{ color: "var(--pos-text-mute)", fontSize: 13, marginBottom: 12 }}>
          {t(
            "posOrdersReturnHintBoth",
            "Enter both Cases (qty) and Weight (kg) — same as the sale — so the farmer sees what was returned. Customer refund includes commission."
          )}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 380, overflowY: "auto" }}>
          {!hasReturnable && (
            <div style={{ textAlign: "center", color: "var(--pos-text-faint)", fontSize: 13, padding: "16px 0" }}>
              {t("posOrdersFullyReturned", "Everything on this sale has already been returned.")}
            </div>
          )}
          {returnableItems.map((it) => {
            const availQ = availableQty(it);
            const availW = availableWeight(it);
            const soldQ = soldQtyOf(it);
            const soldW = soldWeightOf(it);
            const price = unitPriceOf(it);
            const name = it.product
              ? parseLocalizedName(it.product.name, i18n.language, `#${it.product_id}`)
              : `#${it.product_id}`;
            const vendor = it.vendor?.name;
            const maxQ = Math.max(availQ, soldQ, 0);
            const maxW = Math.max(availW, soldW, 0);

            return (
              <div
                key={it.id}
                style={{
                  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                  padding: "12px 12px", border: "1px solid var(--pos-border)", borderRadius: 10,
                  background: "var(--pos-surface-2)",
                }}
              >
                <div style={{ flex: "1 1 160px", minWidth: 140 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "var(--pos-text)" }}>{name}</div>
                  <div style={{ fontSize: 12, color: "var(--pos-text-mute)", marginTop: 2 }}>
                    {vendor ? `${vendor} · ` : ""}
                    {t("vendorSold", "Sold")}:{" "}
                    <b>{soldQ > 0 ? `${soldQ} ${t("posQty", "pcs")}` : "—"}</b>
                    {" · "}
                    <b>{soldW > 0 ? `${soldW} ${t("kg", "kg")}` : "—"}</b>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--pos-text-faint)" }}>
                    {t("posOrdersAvailable", "Available")}: {availQ} {t("posQty", "pcs")} · {availW} {t("kg", "kg")}
                    {" · "}{formatCurrency(price, currency)}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 800 }}>
                    {t("posReturnCases", "Cases (qty)")}
                  </span>
                  <InputNumber
                    min={0}
                    max={maxQ > 0 ? maxQ : undefined}
                    step={1}
                    value={returnQtys[it.id] ?? 0}
                    onChange={(v) => setReturnQtys((prev) => ({ ...prev, [it.id]: v ?? 0 }))}
                    controls={false}
                    style={{ width: 100, background: "#eff6ff", borderColor: "#93c5fd" }}
                    placeholder={t("qty", "qty")}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, color: "#15803d", fontWeight: 800 }}>
                    {t("posReturnWeight", "Weight (kg)")}
                  </span>
                  <InputNumber
                    min={0}
                    max={maxW > 0 ? maxW : undefined}
                    step={0.5}
                    value={returnWeights[it.id] ?? 0}
                    onChange={(v) => setReturnWeights((prev) => ({ ...prev, [it.id]: v ?? 0 }))}
                    controls={false}
                    style={{ width: 100, background: "#f0fdf4", borderColor: "#86efac" }}
                    placeholder={t("kg", "KG")}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setReturnQtys((prev) => ({ ...prev, [it.id]: availQ || soldQ }));
                    setReturnWeights((prev) => ({ ...prev, [it.id]: availW || soldW }));
                  }}
                  style={{
                    border: "1px solid #e5e7eb", background: "#f8fafc", color: "#475569",
                    borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                    alignSelf: "flex-end",
                  }}
                >
                  {t("posOrdersReturnAll", "All")}
                </button>
                <Input
                  placeholder={t("posOrdersReturnReason", "Reason (e.g. bad quality)")}
                  value={returnReasons[it.id] ?? ""}
                  onChange={(e) => setReturnReasons((prev) => ({ ...prev, [it.id]: e.target.value }))}
                  style={{ width: 160, alignSelf: "flex-end" }}
                />
              </div>
            );
          })}
        </div>
        <div
          style={{
            marginTop: 14, padding: "12px 16px", borderRadius: 10,
            background: "#f0fdf4", border: "1px solid #bbf7d0", textAlign: "center",
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 900, color: "#16a34a" }}>
            {t("posOrdersReturnValue", "Return value")}: {formatCurrency(returnCashBack, currency)}
          </div>
          <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 600, marginTop: 6 }}>
            {t("posTerminalSubtotal", "Product")}: {formatCurrency(productCashBack, currency)}
            {orderComm > 0 && (
              <>
                {" + "}
                <span style={{ color: "#d97706" }}>
                  {t("posTerminalCommission", "Commission")}: {formatCurrency(commissionCashBack, currency)}
                </span>
              </>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--pos-text-mute)", marginTop: 4 }}>
            {t("refunded", "Cash refund")}: {formatCurrency(estimatedCashRefund, currency)}
            {" · "}{t("debtReduced", "Debt reduced")}: {formatCurrency(estimatedCreditReduction, currency)}
          </div>
        </div>
        {estimatedCashRefund > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "var(--pos-text-mute)", marginBottom: 5 }}>{t("refundMethod", "Refund method")}</div>
            <Select
              value={returnRefundMethod}
              onChange={setReturnRefundMethod}
              style={{ width: "100%" }}
              options={posPaymentOptions}
              classNames={{ popup: { root: "pos-dropdown-popup" } }}
              getPopupContainer={() => document.body}
            />
          </div>
        )}
      </Modal>

      {/* Cancel reason modal */}
      <Modal
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        title={<span style={{ color: "var(--pos-accent)" }}><StopOutlined /> {t("posOrdersCancelTitle", "Cancel Sale")} {selected?.document_no || formatOrderNo(selected?.id, selected?.created_at)}</span>}
        okText={t("posOrdersCancelOk", "Confirm Cancel")}
        okButtonProps={{ danger: true, loading: cancelling, disabled: cancelNote.trim().length < 3 }}
        onOk={handleCancel}
        zIndex={4000}
        width={400}
        className="pos-sub-modal"
        rootClassName="pos-sub-modal-root"
      >
        <p style={{ color: "var(--pos-text-mute)", fontSize: 13, marginBottom: 12 }}>
          {t("posOrdersCancelWhy", "Why is this sale being cancelled? (required)")}
        </p>
        <Input.TextArea
          rows={3}
          placeholder={t("posOrdersCancelPlaceholder", "Enter cancellation reason...")}
          value={cancelNote}
          onChange={(e) => setCancelNote(e.target.value)}
          style={{ fontSize: 13 }}
        />
        {cancelNote.trim().length < 3 && (
          <div style={{ fontSize: 11, color: "var(--pos-accent)", marginTop: 4 }}>
            {t("posOrdersCancelMinLength", "Enter at least 3 characters to continue")}
          </div>
        )}
        {Number(selected?.amount_paid || 0) - Number(selected?.refunded_amount || 0) > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "var(--pos-text-mute)", marginBottom: 5 }}>{t("refundMethod", "Refund method")}</div>
            <Select
              value={cancelRefundMethod}
              onChange={setCancelRefundMethod}
              style={{ width: "100%" }}
              options={posPaymentOptions}
              classNames={{ popup: { root: "pos-dropdown-popup" } }}
              getPopupContainer={() => document.body}
            />
          </div>
        )}
      </Modal>

      {/* Detail modal */}
      <Modal
        open={!!selected}
        onCancel={() => setSelected(null)}
        title={
          <span style={{ color: "var(--pos-text)" }}>
            {selected?.document_no || formatOrderNo(selected?.id, selected?.created_at)} — {selected?.customer?.name || t("posWalkIn", "Walk-in")}
          </span>
        }
        footer={null}
        width={560}
        zIndex={3000}
        className="pos-sub-modal"
        rootClassName="pos-sub-modal-root"
      >
        {selected && (
          <div className="pos-orders-detail">
            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              {statusBadge(selected.status, selected.payment_method)}
              <span style={{ fontSize: 12, color: "var(--pos-text-faint)" }}>
                {new Date(selected.created_at).toLocaleString()}
              </span>
            </div>

            {/* Items */}
            <div className="pos-orders-items">
              {(selected.items || selected.order_products || []).map((item) => {
                const name = item.product
                  ? parseLocalizedName(item.product.name, i18n.language, `#${item.product_id}`)
                  : `Product #${item.product_id}`;
                const vendorName = item.vendor?.name || "";
                const unitPrice = item.unit_price ?? item.single_price ?? 0;
                return (
                  <div className="pos-orders-item-row" key={item.id}>
                    <div className="pos-orders-item-thumb">
                      {item.product?.image_url
                        ? <img src={getStorageUrl(item.product.image_url)} alt="" />
                        : <ShopOutlined />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "var(--pos-text)" }}>{name}</div>
                      {vendorName && (
                        <div style={{ fontSize: 11, color: "var(--pos-text-faint)" }}>{vendorName}</div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", fontSize: 13, color: "var(--pos-text)" }}>
                      <div style={{ color: "var(--pos-text-mute)" }}>
                        {(() => {
                          const w = parseFloat(item.weight) || 0;
                          const q = parseFloat(item.quantity) || 0;
                          const parts = [];
                          if (q > 0) parts.push(`${q} ${t("posQty", "Qty")}`);
                          if (w > 0) parts.push(`${w} ${t("kg", "KG")}`);
                          return parts.length ? parts.join(" · ") : "—";
                        })()}
                        {" × "}{formatCurrency(unitPrice, currency)}
                      </div>
                      <div style={{ fontWeight: 700, color: "var(--pos-text)" }}>{formatCurrency(item.subtotal ?? item.total_price, currency)}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pos-orders-totals" style={{ borderTop: "2px solid var(--pos-border)", paddingTop: 12, marginTop: 8 }}>
              {(() => {
                const commission = parseFloat(selected.commission_amount || 0);
                const taxed = !(selected.is_taxed === false || selected.is_taxed === 0);
                const total = parseFloat(selected.total_price || 0);
                const subtotal = orderProductSubtotal(selected) || linesBase;
                return (
                  <>
                    {taxed && commission > 0 && (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--pos-text-mute)", marginBottom: 4 }}>
                          <span>{t("posTerminalSubtotal", "Subtotal")}</span>
                          <span style={{ fontWeight: 600, color: "var(--pos-text)" }}>{formatCurrency(subtotal, currency)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--pos-warning)", marginBottom: 6 }}>
                          <span>{t("posTerminalCommission", "Commission")}</span>
                          <span style={{ fontWeight: 700 }}>+ {formatCurrency(commission, currency)}</span>
                        </div>
                      </>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 900, color: "var(--pos-text)" }}>
                      <span>{t("posOrdersTotal", "Total")}</span>
                      <span>{formatCurrency(total, currency)}</span>
                    </div>
                  </>
                );
              })()}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--pos-text-mute)", marginTop: 4 }}>
                <span>{t("posOrdersPayment", "Payment")}</span>
                <span style={{ fontWeight: 600, color: "var(--pos-text)" }}>{paymentLabel(selected.payment_method)}</span>
              </div>
              {parseFloat(selected.total_returned) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--pos-danger)", marginTop: 4 }}>
                  <span>{t("posOrdersReturned", "Returned")}</span>
                  <span style={{ fontWeight: 700 }}>− {formatCurrency(selected.total_returned, currency)}</span>
                </div>
              )}
            </div>

            {selected.cancellation_reason && (
              <div style={{ marginTop: 10, padding: "8px 12px", background: "var(--pos-accent-soft)", borderRadius: 8, borderLeft: "3px solid var(--pos-accent)" }}>
                <div style={{ fontSize: 11, color: "var(--pos-accent)", fontWeight: 700, marginBottom: 2 }}>{t("posOrdersCancellationReason", "Cancellation Reason")}</div>
                <div style={{ fontSize: 13, color: "var(--pos-text)" }}>{selected.cancellation_reason}</div>
                <div style={{ fontSize: 11, color: "var(--pos-text-mute)", marginTop: 3 }}>
                  {selected.cancelled_by?.name || "—"} · {selected.cancelled_at ? new Date(selected.cancelled_at).toLocaleString() : "—"}
                </div>
              </div>
            )}

            {selected._pendingSync && (
              <div style={{ marginBottom: 12, padding: "8px 12px", background: "var(--pos-warning-soft)", borderRadius: 8, borderLeft: "3px solid var(--pos-warning)" }}>
                <div style={{ fontSize: 12, color: "var(--pos-warning)", fontWeight: 700 }}>
                  {t("posOfflinePendingDetail", "Saved offline — will upload when internet returns (kept up to 24 hours).")}
                </div>
                <Button
                  size="small"
                  type="primary"
                  style={{ marginTop: 8 }}
                  onClick={async () => {
                    const result = await syncPendingOfflineSales();
                    if (result.synced > 0) {
                      message.success(t("posOfflineSynced", "Synced {{count}} offline sale(s)", { count: result.synced }));
                      setSelected(null);
                      loadSales();
                    } else if (result.remaining > 0) {
                      message.warning(t("posOfflineStillPending", "Still waiting for a connection"));
                    }
                  }}
                >
                  {t("posOfflineRetrySync", "Retry sync now")}
                </Button>
              </div>
            )}

            {selected._pendingSync && (
              <div className="pos-orders-actions">
                <button
                  type="button"
                  className="pos-orders-action-btn pos-orders-action-btn--print"
                  onClick={() => { setSelected(null); onReprint && onReprint(selected); }}
                >
                  <PrinterOutlined /> {t("posOrdersReprint", "Reprint")}
                </button>
              </div>
            )}

            {selected.status === "confirmed" && !selected._pendingSync && (
              <div className="pos-orders-actions">
                {canEdit && (
                  <button
                    type="button"
                    className="pos-orders-action-btn pos-orders-action-btn--edit"
                    onClick={openFullEdit}
                  >
                    <EditOutlined /> {t("editOrder", "Edit Order")}
                  </button>
                )}
                {canReturn && (
                  <button
                    type="button"
                    className="pos-orders-action-btn pos-orders-action-btn--return"
                    onClick={openReturnModal}
                  >
                    <RollbackOutlined /> {t("posOrdersReturn", "Return")}
                  </button>
                )}
                {canCancel && (
                  <button
                    type="button"
                    className="pos-orders-action-btn pos-orders-action-btn--cancel"
                    onClick={() => {
                      setCancelNote("");
                      setCancelRefundMethod(selected.payment_method === "cash" ? "cash" : "credit");
                      setCancelModalOpen(true);
                    }}
                  >
                    <StopOutlined /> {t("posOrdersCancel", "Cancel")}
                  </button>
                )}
                <button
                  type="button"
                  className="pos-orders-action-btn pos-orders-action-btn--print"
                  onClick={() => { setSelected(null); onReprint && onReprint(selected); }}
                >
                  <PrinterOutlined /> {t("posOrdersReprint", "Reprint")}
                </button>
              </div>
            )}

            {canDelete && selected.status === "draft" && Number(selected.amount_paid || 0) <= 0 && (
              <div style={{ display: "flex", marginTop: 10 }}>
                <button
                  type="button"
                  disabled={deleting}
                  className="pos-orders-action-btn pos-orders-action-btn--return"
                  style={{ flex: 1, height: 40 }}
                  onClick={handleDelete}
                >
                  <DeleteOutlined /> {t("archive", "Archive")}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PosSubOrders;
